import * as T from 'three';
import {applyGarmentPose} from './garmentPose';
import type {DeathContactResolver,DeathRagdoll} from './deathPhysics';

interface Probe {mesh:T.SkinnedMesh;vertex:number;previous:T.Vector3;}
/** A cached support hull of the visible outfit, not thousands of per-frame
 * triangle queries. Joint spheres do the main solve; these extremal vertices
 * stop noses, fingers and boots from extending through the contact surface. */
export class RagdollSurface {
  readonly probes:Probe[]=[];
  private readonly inverse=new T.Matrix4();
  private readonly point=new T.Vector3();
  private readonly corrected=new T.Vector3();
  private readonly normal=new T.Vector3();
  private readonly correction=new T.Vector3();
  private readonly contactNormal=new T.Vector3();
  private readonly meshes=new Set<T.SkinnedMesh>();
  constructor(private readonly root:T.Object3D,meshes:readonly T.SkinnedMesh[],joints:readonly T.Bone[]){
    root.updateWorldMatrix(true,true);this.inverse.copy(root.matrixWorld).invert();
    const directions:T.Vector3[]=[];
    for(const x of [-1,0,1])for(const y of [-1,0,1])for(const z of [-1,0,1])if(x||y||z)directions.push(new T.Vector3(x,y,z).normalize());
    const lookup=new Map<T.Bone,{matrix:T.Matrix4;scores:number[];supports:(Probe|null)[]}>();
    for(const joint of joints)lookup.set(joint,{matrix:joint.matrixWorld.clone().invert(),scores:directions.map(()=>-Infinity),supports:directions.map(()=>null)});
    const local=new T.Vector3();
    for(const mesh of meshes){
      if(!mesh.visible||(!Array.isArray(mesh.material)&&!mesh.material.visible))continue;
      applyGarmentPose(mesh);const g=mesh.geometry,index=g.index,count=index?.count??g.attributes.position.count;
      const start=Math.max(0,g.drawRange.start),end=Math.min(count,start+g.drawRange.count),skinIndex=g.attributes.skinIndex,skinWeight=g.attributes.skinWeight;
      if(!skinIndex||!skinWeight)continue;
      const materials=mesh.material;
      const ranges=Array.isArray(materials)?g.groups.filter(group=>materials[group.materialIndex??0]?.visible!==false).map(group=>[Math.max(start,group.start),Math.min(end,group.start+group.count)]):[[start,end]];
      const seen=new Set<number>();
      for(const [start,end]of ranges)for(let i=start;i<end;i++){
        const vertex=index?index.getX(i):i;if(seen.has(vertex))continue;seen.add(vertex);
        let weight=-1,boneIndex=0;
        for(let component=0;component<4;component++){const w=skinWeight.getComponent(vertex,component);if(w>weight){weight=w;boneIndex=skinIndex.getComponent(vertex,component);}}
        let bone:T.Object3D|null=mesh.skeleton.bones[boneIndex];while(bone&&!lookup.has(bone as T.Bone))bone=bone.parent;
        const group=lookup.get((bone??joints[0])as T.Bone)!;
        mesh.getVertexPosition(vertex,this.point).applyMatrix4(mesh.matrixWorld);local.copy(this.point).applyMatrix4(group.matrix);
        for(let d=0;d<directions.length;d++){
          const score=local.dot(directions[d]);if(score<=group.scores[d])continue;
          group.scores[d]=score;group.supports[d]={mesh,vertex,previous:this.point.clone().applyMatrix4(this.inverse)};
        }
      }
    }
    const unique=new Set<string>();
    for(const group of lookup.values())for(const probe of group.supports)if(probe){const key=`${probe.mesh.uuid}:${probe.vertex}`;if(!unique.has(key)){unique.add(key);this.probes.push(probe);this.meshes.add(probe.mesh);}}
  }
  /** The strongest real support contact translates the constrained body as one
   * unit. It never changes bone lengths or pushes free-falling joints apart. */
  resolve(body:DeathRagdoll,contact:DeathContactResolver,dt:number):boolean{
    this.root.updateWorldMatrix(true,true);this.inverse.copy(this.root.matrixWorld).invert();
    for(const mesh of this.meshes)applyGarmentPose(mesh);
    this.correction.set(0,0,0);let depth=1e-10;
    for(const probe of this.probes){
      probe.mesh.getVertexPosition(probe.vertex,this.point).applyMatrix4(probe.mesh.matrixWorld).applyMatrix4(this.inverse);
      this.corrected.copy(this.point);
      if(contact(probe.previous,this.corrected,.012,this.normal)){
        this.corrected.sub(this.point);const squared=this.corrected.lengthSq();
        if(squared>depth){depth=squared;this.correction.copy(this.corrected);this.contactNormal.copy(this.normal);}
      }
    }
    if(depth<=1e-10)return false;
    body.surfaceContact(this.correction,this.contactNormal,dt);return true;
  }
  remember():void{
    this.root.updateWorldMatrix(true,true);this.inverse.copy(this.root.matrixWorld).invert();
    for(const probe of this.probes)probe.mesh.getVertexPosition(probe.vertex,probe.previous).applyMatrix4(probe.mesh.matrixWorld).applyMatrix4(this.inverse);
  }
}
